import { formatPrice, IPayPlanFiltering } from '@sureapp/platform-sdk';
import { TInputSelectOption } from '@sureapp/wl-toggle-common/dist/components/InputSelect';
import { isServer } from '@sureapp/wl-toggle-common/dist/utils/env';
import { prepUrlForEnvironment } from '@sureapp/wl-toggle-common/dist/utils/url';
import { Link } from 'react-router-dom';

import {
  InfoIconOutline as TenantInfoIconOutline,
  InfoIconSolid as TenantInfoIconSolid,
} from '@@/auto/components/InfoIcon';
import KnockoutCopy from '@@/auto/components/KnockoutCopy/KnockoutCopy';
import { AUTO_DISCOUNTS } from '@@/auto/constants/autoDiscounts';
import { featureFlag } from '@@/auto/constants/featureFlags';
import { TConfig, TConfigParams, TGetAddOns } from '@@/auto/interfaces';
import { handleFunctionConfig } from '@@/auto/services/handleMergeConfigs';
import { excludeFalse } from '@@/auto/utils/excludeFalse';
import { ReactComponent as CaretIcon } from '@@/common/assets/caret.svg';
import { ReactComponent as SuccessIcon } from '@@/common/assets/checkmark.svg';
import hatchbackDogImgPng from '@@/common/assets/Hatchback_dog5.png';
import hatchbackDogImgWebP from '@@/common/assets/Hatchback_dog5.webp';
import { ReactComponent as AlertIcon } from '@@/common/assets/toggle/image/alert.svg';
import { ReactComponent as BankPaymentIcon } from '@@/common/assets/toggle/image/bank-payment-icon.svg';
import { ReactComponent as RentersPolicyIcon } from '@@/common/assets/toggle/image/building.svg';
import builtOnSureLogo from '@@/common/assets/toggle/image/built-on-sure-logo.svg';
import { ReactComponent as AutoPolicyIcon } from '@@/common/assets/toggle/image/car.svg';
import { ReactComponent as IdIcon } from '@@/common/assets/toggle/image/card.svg';
import { ReactComponent as CreditCardIcon } from '@@/common/assets/toggle/image/credit.svg';
import idCardsIconSrc from '@@/common/assets/toggle/image/document-multiple.png';
import policyIconSrc from '@@/common/assets/toggle/image/document-single.png';
import { ReactComponent as EnrollInAutoPayIcon } from '@@/common/assets/toggle/image/enroll-in-autopay.svg';
import errorImg from '@@/common/assets/toggle/image/errorImg.png';
import { ReactComponent as PayByEscrowIcon } from '@@/common/assets/toggle/image/escrow-pay-icon.svg';
import { ReactComponent as HomeownersPolicyIcon } from '@@/common/assets/toggle/image/home.svg';
import { ReactComponent as MakePaymentIcon } from '@@/common/assets/toggle/image/make-payment-icon.svg';
import { ReactComponent as NewCreditCardIcon } from '@@/common/assets/toggle/image/new-credit-card.svg';
import { ReactComponent as PaymentAutoIcon } from '@@/common/assets/toggle/image/payment-auto-icon.svg';
import tenantLoveImg from '@@/common/assets/toggle/image/protect-loved-ones.svg';
import tenantAssetsImg from '@@/common/assets/toggle/image/protect-your-assets.svg';
import tenantVehicleImg from '@@/common/assets/toggle/image/protect-your-vehicle.svg';
import { ReactComponent as ShieldIcon } from '@@/common/assets/toggle/image/shield.svg';
import { ReactComponent as InverseToggleLogo } from '@@/common/assets/toggle/image/TAFC-inverse.svg';
import { ReactComponent as LightToggleLogo } from '@@/common/assets/toggle/image/TAFC-ongreen_1.svg';
import { ReactComponent as TenantCarIcon } from '@@/common/assets/toggle/image/toggle-car-front.svg';
import tenantCityImg from '@@/common/assets/toggle/image/toggle-little-city-lilo.png';
import mfaImage from '@@/common/assets/toggle/image/toggle-mfa.svg';
import { ReactComponent as RoadsideAssistanceIcon } from '@@/common/assets/toggle/image/towtruck.svg';
import ufoImg from '@@/common/assets/ufo.png';
import StandardHero from '@@/common/components/LayoutSignUp/heroes/StandardHero';
import { buildPublicAssetPath } from '@@/common/utils';

import regionConfigMerge from '../services/regionConfigMerge';

const baseUrl = prepUrlForEnvironment({
  url: process.env.REACT_APP_ACCOUNT_BASE_URL,
});
const mfaSuccessHost = prepUrlForEnvironment({
  url: process.env.REACT_APP_MFA_SUCCESS_REDIRECT_HOST,
});

const defaultVehicleSideEffectMessage =
  'This coverage is only available if Comprehensive & Collision is also selected.';

function generateConfig({ isFeatureEnabled }: TConfigParams): TConfig {
  return {
    antiTheftCopyConfig: {
      label: 'Does this vehicle have an anti-theft device?',
      explanationText:
        'Anti-theft mechanisms are any passive or active anti-theft devices permanently installed, either as original equipment from the manufacturer or as after market equipment on the vehicle.',
    },
    packagedCoveragesCopy: {
      label: 'First party benefits combination package',
      explanationText:
        'Accidents can be scary, especially when you or someone you love is injured. If that happens, though, this combination of coverages can help lessen the impact to your wallet (or theirs).',
    },
    auto: {
      addOns: {
        options: {
          gap: {
            copy: {
              label: 'Gap Coverage',
              description:
                "Accidents don't wait for your car to be paid off. If your car is totaled, this coverage can help with any remaining loan or lease amount you owe.",
              modalContent:
                'This coverage is optional and can help pay for the additional amount you owe on a vehicle loan or lease beyond the insurance coverage for a vehicle that is totaled.',
            },
          },
          glass: {
            copy: {
              label: 'Glass & Safety',
              description:
                'Would you like to have a $0 deductible on glass only claims?',
              modalContent:
                "For an additional premium, you can choose a $0 deductible for glass only claims. This includes windshields and windows as well as damage to plastic or other material used in your vehicle's lights.",
            },
          },
          loan_lease_payoff: {
            copy: {
              label: 'Residual Debt (loan payoff)',
              description:
                "Accidents don't wait for your car to be paid off. If your car is totaled, this coverage can help with any remaining loan or lease amount you owe.",
              modalContent:
                'This coverage is optional and can help pay for the additional amount you owe on a vehicle loan or lease beyond the insurance coverage for a vehicle that is totaled.',
            },
          },
          loss_of_use: {
            copy: {
              label: 'Loss of Use',
              description:
                "While you're without your car after an accident, this coverage helps provide the alternate transportation you need. It's optional but if something happens to your car, ask yourself if you really want to count on your buddy getting you to work.",
              modalContent:
                'Loss of Use is optional and provides a flat amount per day up to the policy limit for alternative transportation such as ridesharing, car rental, taxicab, or public transit while your vehicle is being repaired or replaced following a covered loss.',
            },
          },
          new_car_replacement: {
            copy: {
              label: 'New Car Replacement',
              description:
                "Nothing stinks more than your beautiful new car getting totaled except maybe getting only the depreciated value of it back. This coverage can help get you a brand new replacement so it's like it never happened.",
              modalContent:
                'New Car Replacement is an optional coverage that pays for the cost of replacing your vehicle with a brand new one of the same make and model rather than the depreciated value of yours, if it is totaled and less than two years old.',
            },
          },
          oem_parts: {
            copy: {
              label: 'Original Parts Replacement',
              description:
                "If you're passionate about your car (i.e., it has a name) and want only factory part replacements in the event of an accident, then this is the coverage for you (and Princess Priscilla von Prius).",
              modalContent:
                'This is an optional coverage that pays for original factory equipment replacement parts in the event that your vehicle is damaged in a covered loss.',
            },
          },
          pet_passenger: {
            copy: {
              label: 'Pet Passenger ℠',
              description:
                "No one loves a good car ride more than Fido, but if you're taking him along you might want to add this coverage. It's optional but Fido's big puppy dog eyes are telling you to think twice about going without.",
              modalContent:
                'Pet Passenger is an optional coverage that pays for the cost of veterinary care, burial, disposal, injury, or death of household pets resulting from a covered loss, up to a aggregate maximum limit of $1,000.',
            },
          },
          ridesharing: {
            copy: {
              label: 'Ridesharing',
              description:
                "If you're earning extra dough through rideshare or food delivery, this coverage is designed to complement other insurance that may be provided. Our lives can be pretty blurred these days - one minute you're driving to your friend's house and the next you're dropping off a food order, so add additional protection.",
              modalContent: [
                'Ridesharing is an optional coverage and is applicable if you earn a side income (part-time employment, earning up to $25,000 annually) doing rideshare or on-demand food delivery.',
                "If you're a rideshare driver, it extends the coverage shown on your Declarations Page from the time when you're available to receive requests to the time you're engaged in a prearranged ride. Once the prearranged ride has begun, it extends the coverage shown on your Declarations Page except Liability.",
                "If your side hustle is on-demand food delivery, it extends the coverage shown on your Declarations Page from the time you're available to receive a food delivery request to the time you're engaged in a prearranged delivery. Once the prearranged delivery has begun, it extends the coverage shown on your Declarations Page with two exceptions - Liability coverage up to the minimum limits mandated by your state and Comprehensive & Collision coverage up to $1,000 or the deductible you selected, whichever is higher.",
              ],
            },
          },
          side_hustle: {
            copy: {
              label: 'Side Hustle - Auto',
              description:
                "If you're earning extra dough through rideshare or food delivery, this coverage is designed to complement other insurance that may be provided. Our lives can be pretty blurred these days - one minute you're driving to your friend's house and the next you're dropping off a food order, so add additional protection.",
              modalContent: [
                'Side Hustle Auto is an optional coverage and is applicable if you have part-time employment, earning up to $25,000 annually doing rideshare or on-demand food delivery.',
                `If you're a rideshare driver, it extends the coverage shown on your Declarations Page from the time when you're available to receive requests to the time you're engaged in a prearranged ride. Once the prearranged ride has begun, it extends the coverage shown on your Declarations Page except Liability.`,
                `If your side hustle is on-demand food delivery, it extends the coverage shown on your Declarations Page from the time you're available to receive a food delivery request to the time you're engaged in a prearranged delivery. Once the prearranged delivery has begun, it extends the coverage shown on your Declarations Page with two exceptions - Liability coverage up to the minimum limits mandated by your state and Comprehensive & Collision coverage up to $1,000 or the deductible you selected, whichever is higher.`,
                'In the event of a covered loss to your vehicle, Side Hustle Auto also covers property damage to your business personal property up to $1,000 as well as loss of business income ($500 per week for a maximum of 4 weeks) if you have purchased Comprehensive & Collision coverage. One claim allowed per policy period.',
              ],
            },
          },
          temporary_ride: {
            copy: {
              label: 'Temporary Ride Coverage',
              description:
                "While you're without your car after an accident, this coverage helps provide the alternate transportation you need. It's optional but if something happens to your car, ask yourself if you really want to count on your buddy getting you to work.",
              modalContent:
                'Temporary Ride Coverage is optional and provides a flat amount per day up to the policy limit for alternative transportation such as ridesharing, car rental, taxicab, or public transit while your vehicle is being repaired or replaced following a covered loss.',
            },
            options: {
              optionsCopy: 'Select your per day coverage amount.',
              shouldShowOptions: true,
            },
          },
          towing_road_services: {
            copy: {
              label: 'Towing & Road Service(s)',
              description:
                "Getting stranded with a flat is no fun. Especially if you don't know a jack from a lug wrench. The coverage might be optional but pot holes in winter are not.",
              modalContent:
                'Towing & Road Service(s) is an optional coverage and pays for the cost of towing and other roadside services like a locksmith, flat tire changing, and battery jumpstart in the event your vehicle is disabled (subject to the Service Call conditions).',
            },
          },
        },
        getAddOns: ({ userAction }: Parameters<TGetAddOns>[0]) =>
          excludeFalse([
            'towing_road_services',
            'temporary_ride',
            'side_hustle',
            'gap',
            'oem_parts',
            'new_car_replacement',
            userAction === 'editing-addons' && 'pet_passenger',
          ] as const),
      },
    },
    billingCadenceConfig: {
      canRateChangeWithCadence: false,
      monthly: {
        billingCadenceSubheader: (paymentDateWithOrdinal?: string) => {
          return `Your first payment will be deducted today. Future payments will be
          deducted on the ${paymentDateWithOrdinal} of every month (or the closest
          available day prior to that day).`;
        },
      },
      semi_annual: {
        billingCadenceSubheader:
          'First payment will be deducted today, your next payment will be in 6 months, when your policy is up for renewal.',
      },
    },
    coverageMarkOverrides: {},
    disclosureConfig: {
      disclosureText: '',
    },
    theftFeeConfig: {
      valueText: 'per vehicle one-time fee',
    },
    brazeConfig: {
      isBrazeEnabled: true,
    },
    payment: {
      getPaymentCopy: (paymentMethodType: string) => {
        if (paymentMethodType === 'bank_account') {
          return 'We currently accept credit, debit, checking or savings account as forms of payment.';
        }
        return 'We currently accept credit and debit as forms of payment';
      },
    },
    billingCadenceModalText: {
      billingCadenceModalSubheader:
        'Updating when you pay is easy. Here’s what you need to know.',
      monthlyToFullTopText: (
        <>
          If you switch from <strong>Monthly</strong> to{' '}
          <strong>Pay in full</strong> now, you’re agreeing to pay the rest of
          your policy term today.
        </>
      ),
      getFullToMonthlyTopText: (
        renewalDateWithOrdinal: string,
        isPayPlanFilteringEnabled: boolean
      ) =>
        isPayPlanFilteringEnabled ? (
          <>
            If you switch your payment plan from <strong>Pay in Full</strong> to{' '}
            <strong>Monthly</strong>, your monthly payments will be due on the{' '}
            <strong>{renewalDateWithOrdinal} of each month</strong> (or the
            closest business day prior to that day).
          </>
        ) : (
          <>
            If you switch from <strong>Pay in full</strong> to{' '}
            <strong>Monthly</strong>, you’ll be billed on the{' '}
            {renewalDateWithOrdinal} of every month.
          </>
        ),
      noAmountDueText: (
        <>
          <strong>$0.00</strong> will be charged today, because you have made
          the last payment for your current policy term.
        </>
      ),
      getFullToMonthlyRenewalAmountDueInFutureText: (
        renewalDateWithoutOrdinal: string,
        renewalDateWithOrdinal: string,
        renewalAmountDue: string,
        payPlanFiltering?: IPayPlanFiltering
      ) => (
        <>
          <strong>
            {formatPrice(
              payPlanFiltering?.subtotal_amount_upfront || renewalAmountDue
            )}
          </strong>{' '}
          will be charged on {renewalDateWithoutOrdinal}, when your policy
          renews. Future payments of{' '}
          {formatPrice(payPlanFiltering?.total_subsequent_monthly_amount || '')}{' '}
          will be charged on the {renewalDateWithOrdinal} of every month (or the
          closest available day prior to that day).
        </>
      ),
      getTwoOrFourPayToFullRenewalAmountDueInFutureText: (
        renewalDateWithoutOrdinal: string,
        renewalAmountDue: string
      ) => (
        <>
          {Number(renewalAmountDue) > 0 ? (
            <>
              <strong>{formatPrice(renewalAmountDue)}</strong> will be charged
              on {renewalDateWithoutOrdinal}, when your policy renews.
            </>
          ) : null}
        </>
      ),
      getMonthlyToFullRenewalAmountDueInFutureText: (
        renewalDateWithoutOrdinal: string,
        renewalAmountDue: string
      ) => (
        <>
          {Number(renewalAmountDue) > 0 ? (
            <>
              <strong>{formatPrice(renewalAmountDue)}</strong> will be charged
              on {renewalDateWithoutOrdinal}, when your policy renews.
            </>
          ) : null}
        </>
      ),
      getAnyPlanToPayInFullWhiteBoxText: (amountDue: string) => (
        <>
          {Number(amountDue) > 0 ? (
            <>
              <strong>{formatPrice(amountDue)}</strong> will be charged today,
              when your policy switches to <em>Pay in Full</em>.
            </>
          ) : null}
        </>
      ),
      getTwoPayFourPayToMonthlyWhiteBoxText: (
        renewalAmountDue: string,
        renewalDateWithOrdinal: string,
        renewalDateWithoutOrdinal: string
      ) => (
        <>
          {Number(renewalAmountDue) > 0 ? (
            <>
              <strong>{formatPrice(renewalAmountDue)}</strong> will be charged
              on the {renewalDateWithOrdinal} of every month — starting on{' '}
              {renewalDateWithoutOrdinal}, when your policy will renew.
            </>
          ) : null}
        </>
      ),
      getPayInFullToMonthlyWhiteBoxExtraText: () => '',
    },
    sanitizeCoverageRegexRecord: {},
    sortCoverages: {
      uninsuredMotoristBodilyInjury: (
        a: TInputSelectOption,
        b: TInputSelectOption
      ) =>
        a.value.toString().localeCompare(b.value, undefined, { numeric: true }),
    },
    driverFormConfig: {
      additionalFields: [],
      distantStudentConfig: {
        label: ({ subjectText }) =>
          `Is ${subjectText} a full-time student away at college?`,
        explanationText: 'Living more than 100 miles from home',
      },
      goodStudentConfig: {
        label: ({ subjectText }) =>
          `Is ${subjectText} a full-time student with good academic standing (3.0 GPA/B average or better)?`,
        explanationText: '3.0 grade point average (B average or better)',
      },
      vehicleCustomarilyDrivenConfig: {
        label: (subjectText) =>
          `What vehicle does ${subjectText} customarily drive?`,
        explanationText: `Please select the vehicle this person drives most often. You can choose the same vehicle for more than one driver if you need to.`,
      },
    },
    vehicleFormConfig: {
      additionalFields: ['purchase_year'],
    },
    errorBoundaryConfig: {
      email: 'support@gettoggle.com',
    },
    fetchingViewConfig: {
      defaultAdpfHeader:
        'Sit back and relax while we create your personalized quote.',
      defaultMvrHeader: 'Buckle up.',
      defaultSubheader:
        'We do our best to match your current policy and provide an instant quote.',
    },
    collectEmailViewConfig: {
      header: `Last up, let's get you a quote.`,
      subHeader: `Providing your email address also helps us see if you qualify for certain discounts.`,
      isEnabled: true,
    },
    collectPhoneViewConfig: {
      isEnabled: false,
    },
    cmsSlugs: {
      authenticatedFooterSlug: 'authenticated-footer',
      copyrightSlug: 'copyright',
      footerSlug: 'global-footer',
      privacyPolicySlug: isFeatureEnabled(
        featureFlag.IS_PRIVACY_POLICY_V2_ENABLED
      )
        ? 'toggle-privacy-policy-v2'
        : 'privacy-policy',
      termsViewSlug: 'terms-and-conditions',
      billingViewSlug: 'billing',
    },
    coverage: {
      bodilyInjury: {
        title: 'Bodily Injury Liability',
        description:
          "Accidents happen. So do injuries to other people. Help protect yourself in the event you're found at fault.",
        modalContent:
          'Bodily Injury (BI) coverage pays for damages you or the driver of your vehicle are legally obligated to pay due to accidental bodily injury to others, and the injury occurred due to a covered loss. This is a mandatory coverage and the limits chosen are the maximum the company will pay for any single occurrence.',
      },
      propertyDamage: {
        title: 'Property Damage Liability',
        description:
          "You don't really get to pick which car you accidentally rear-end - whether it's a beater or a bimmer, this coverage can help.",
        modalContent:
          "Property damage (PD) coverage pays for damages if you or the driver of your vehicle are found legally obligated to pay for accidental property damage to someone else's property due to a covered loss, up to the limit of coverage chosen by you. This is a mandatory coverage.",
      },
      compColl: {
        description: `We combined these coverages because if you depend on your car, you probably don't want to have to worry about the variety of scenarios (hail storm, fender bender, and broken windshield are just a few examples). Required coverage for leased or financed vehicles.`,
        modalContent:
          'Comprehensive pays up to the limits chosen for accidental damage to your car from things other than a collision - like floods, hail, fire, theft, and windshield or animal damage. Collision pays up to the limits chosen for accidental damage to your car caused by collision with another vehicle or fixed object. Deductibles chosen are per vehicle.',
        title: 'Comprehensive & Collision',
      },
      medical: {
        title: 'Medical',
        description:
          'Accidents can be scary, especially when you or someone you love is injured. If that happens, though, this coverage can help lessen the impact to your wallet (or theirs).',
        modalContent:
          'Medical coverage pays reasonable medical and funeral expenses incurred by an insured person on your policy caused by an accident, up to the limit selected and regardless of fault.',
        textAlertContent: '',
      },
      underinsuredMotoristPropertyDamage: {
        description:
          'You can hope that all other drivers have enough insurance to pay for damage they cause to your car. Or you can protect yourself with this coverage, which helps cover the difference.',
        modalContent:
          'Underinsured Motorist Property Damage (UIMPD) pays any gap between the insurance available to the at-fault driver and the limits chosen, for amounts that you are legally entitled to recover for physical damage to your insured car, sustained in an accident caused by a driver who has insufficient insurance.',
        title: 'Underinsured Motorist Property Damage',
      },
      uninsuredMotoristPropertyDamage: {
        description: `Your bumper sticker might say "don't hit me if you're uninsured" but it isn't actually protecting you against that. Don't worry, this coverage can.`,
        modalContent:
          'Uninsured Motorist Property Damage (UMPD) pays, up to the limits chosen, the amount you are legally entitled to recover for physical damages to your insured car sustained in an accident caused by a driver who is not insured.',
        title: 'Uninsured Motorist Property Damage',
      },
      underinsuredMotoristBodilyInjury: {
        title: 'Underinsured Motorist Bodily Injury',
        description:
          'If you or your passenger is injured in an accident and the driver at fault has insurance but not quite enough, this coverage can help make up the difference.',
        modalContent:
          'Underinsured Motorist Bodily Injury (UIMBI) pays for damages that an insured person on your policy is legally entitled to recover for bodily injury or death caused by an at-fault driver with insufficient insurance.',
        textAlertContent:
          'We’ve decreased your Underinsured motorist bodily injury and Underinsured motorist bodily injury limits to match the changes you’ve made to your Bodily injury coverage. ',
      },
      uninsuredMotoristBodilyInjury: {
        title: 'Uninsured Motorist Bodily Injury',
        description:
          "That pain in your neck is not just from the accident - it's also from finding out that the person who hit you has no insurance. This coverage can help.",
        modalContent:
          'Uninsured Motorist Bodily Injury (UMBI) pays the amount you are legally entitled to recover for damages from bodily injury, up to the limit of liability, when an insured person on your policy is hit by an uninsured driver or an unidentified (e.g. hit-and-run) driver.',
        textAlertContent:
          'We’ve decreased your Uninsured motorist bodily injury and Underinsured motorist bodily injury limits to match the changes you’ve made to your Bodily injury coverage.',
      },
    },
    coverageConfig: {
      header: `Let's customize your coverage.`,
      subHeader: `To start, we've made selections based on your current policy. Adjust things to get the exact coverage you want. Or just skip to the next step.`,
      tenantAssetsImg,
      tenantLoveImg,
      tenantVehicleImg,
      getOverviewLineTextVariant: () => 'h5',
      vehicleCoverageHeaderCopy: {
        description:
          'How important is your car to you? How much do you depend on it? If something happens to it, these coverages will help get you back on the road.',
        image: 'vehicle',
        title: 'Protect your vehicles',
      },
      protectAssetsCoverageHeaderCopy: {
        description:
          'These coverages help protect you and your assets from damages that you caused. They can be the difference between a bad day and a bad decade. ',
        image: 'assets',
        title: 'Protect your assets',
      },
    },
    coveragesInputTypeRecord: {
      medical_limit: () => 'slider',
      additional_equipment: () => 'radio',
      rental_reimbursement: () => 'radio',
    },
    discountsConfig: {},
    enabledSideEffects: [],
    editPolicyReviewConfig: {
      editButtonVariant: 'ACTION_SECONDARY',
    },
    errorPageConfig: {
      backgroundImgSrc: errorImg,
    },
    excludeDriversViewConfig: {
      submitButtonLabel: 'Next',
      label: 'Would you like to exclude this driver and continue?',
      explanationText:
        'An excluded driver will not be covered when driving any vehicles listed on the policy. If you choose not to exclude this driver, you will not be eligible for coverage with Toggle.',
    },
    excludeVehicleViewConfig: {
      submitButtonLabel: 'Next',
    },
    featureFlags: {
      isAccountManagementEnabled: isFeatureEnabled(
        featureFlag.IS_TOGGLE_ACCOUNT_MANAGEMENT_ENABLED
      ),
      isAutoPurchaseFlowEnabled: isFeatureEnabled(
        featureFlag.IS_TOGGLE_AUTO_PURCHASE_FLOW_ENABLED
      ),
      isAutoQuoteLinkExchangeEnabled: isFeatureEnabled(
        featureFlag.IS_TOGGLE_AUTO_QUOTE_LINK_EXCHANGE_ENABLED
      ),
      isDirectToAutoQuoteEnabled: isFeatureEnabled(
        featureFlag.IS_TOGGLE_DIRECT_TO_AUTO_QUOTE_ENABLED
      ),
      isLogoRedirectInAutoPurchaseFlowEnabled: isFeatureEnabled(
        featureFlag.IS_TOGGLE_LOGO_REDIRECT_IN_AUTO_PURCHASE_FLOW_ENABLED
      ),
      isPrivacyAndTermsEnabled: isFeatureEnabled(
        featureFlag.IS_TOGGLE_PRIVACY_AND_TERMS_ENABLED
      ),
      isQuoteAssistForTenantEnabled:
        isFeatureEnabled(featureFlag.IS_QUOTE_ASSIST_ENABLED) &&
        isFeatureEnabled(featureFlag.IS_TOGGLE_QUOTE_ASSIST_ENABLED),
      isRentersQuoteCreationEnabled: isFeatureEnabled(
        featureFlag.IS_TOGGLE_RENTERS_QUOTE_CREATION_ENABLED
      ),
      isSelfServiceEnabled: isFeatureEnabled(
        featureFlag.IS_TOGGLE_SELF_SERVICE_ENABLED
      ),
      isHomeownersEnabled: isFeatureEnabled(featureFlag.IS_HOMEOWNERS_ENABLED),
      isAutoPromotionEnabledForHomeowners: isFeatureEnabled(
        featureFlag.IS_AUTO_PROMOTION_ENABLED_FOR_HOMEOWNERS
      ),
      isBillingEnabled: isFeatureEnabled(featureFlag.IS_TOGGLE_BILLING_ENABLED),
      isShowingSelfServicePromoCard: true,
    },
    footerConfig: {
      hasNavItems: true,
      header: null,
      sureFooterLogoSrc: builtOnSureLogo,
      tenantPhoneVariant: 'first-row',
      getSocialLinks: () => [
        {
          href: 'https://www.facebook.com/gettoggle',
          fabIcon: 'fa-facebook-f',
          label: 'Facebook',
        },
        {
          href: 'https://www.instagram.com/gettoggle',
          fabIcon: 'fa-instagram',
          label: 'Instagram',
        },
      ],
    },
    iconConfig: {
      isShowAlertIcon: true,
      isShowDriverIcon: true,
      isShowPersonIcon: true,
      isShowVehicleIcon: true,
    },
    icons: {
      CaretIcon,
      SuccessIcon,
      TenantInfoIconSolid,
      TenantInfoIconOutline,
      TenantCarIcon,
      AlertIcon,
      BankPaymentIcon,
      EnrollInAutoPayIcon,
      NewCreditCardIcon,
      CreditCardIcon,
      PaymentAutoIcon,
      autoPolicyIcons: {
        AutoPolicyIcon,
        IdIcon,
        RoadsideAssistanceIcon,
        idCardsIconSrc,
        policyIconSrc,
        MakePaymentIcon,
      },
      rentersPolicyIcons: {
        RentersPolicyIcon,
      },
      homeownersPolicyIcons: {
        HomeownersPolicyIcon,
        ShieldIcon,
        PayByEscrowIcon,
      },
      trustIcons: [
        'pf-visa',
        'pf-mastercard-alt',
        'pf-american-express-alt',
        'pf-discover',
        'pf-jcb',
        'pf-diners',
      ],
    },
    iidKnockoutCopy: {
      featureFlag: 'isUpdatedToggleKnockoutCopy',
      header:
        "We're sorry but we are unable to provide a quote for you at this time.",
      subHeader: <KnockoutCopy phoneNumber="877-249-0086" />,
    },
    ineligibleAddressConfig: {
      getCancellationCopy: ({ date }: { date?: string }) => (
        <>
          All right, your cancellation date will be today,{' '}
          <strong>{date}</strong>.
        </>
      ),
      getEligibilityText: ({
        isNewRegion,
        newState,
        oldState,
      }: Parameters<
        NonNullable<TConfig['ineligibleAddressConfig']['getEligibilityText']>
      >[0]) =>
        isNewRegion
          ? `Nice! Toggle covers ${newState}. We can cancel your ${oldState} policy and start your new ${newState} policy today.`
          : `Sorry! Toggle isn't available in ${newState}. But we'll help you cancel your policy if you'd like.`,
    },
    layoutContentConfig: {
      tenantCityImg,
    },
    layoutQuoteConfig: {
      isLogoHeader: false,
      summaryPosition: 'fixed',
      variant: '100vh-quote-summary',
      summaryCopy: 'Your auto insurance quote',
    },
    layoutSelfServiceConfig: {
      backgroundImgSrc: buildPublicAssetPath('img/town.svg'),
    },
    localforageDbName: 'toggle',
    loginConfig: {
      backgroundImgSrc: buildPublicAssetPath('img/hello-illustration.svg'),
      isLightLogo: true,
    },
    logos: {
      default: InverseToggleLogo,
      light: LightToggleLogo,
      title: 'Toggle Logo',
      toggleDefault: null,
      toggleLight: null,
    },
    mfaConfig: {
      mfaImage,
    },
    missingLicenseViewConfig: {
      submitButtonLabel: 'Next',
    },
    missingLicenseInfoViewConfig: {
      submitButtonLabel: 'Next',
    },
    missingLienholderViewConfig: {
      submitButtonLabel: 'Next',
    },
    missingVinViewConfig: {
      submitButtonLabel: 'Next',
    },
    modalAdjustedPriceConfig: {
      footerButtonVariant: 'SECONDARY',
      footerPrimaryButtonCopy: 'Ok, thanks',
    },
    modalRemoveDriverConfig: {
      title: 'Remove driver',
      header: ({ firstName }) =>
        `Are you sure you want to remove ${firstName}?`,
      subheader:
        'If removed, they will no longer be listed on your policy and may be considered an Undisclosed Driver. Therefore, in the event of a loss, they would not be insured on your policy, resulting in no coverage or a reduction of coverage. Do you want to proceed?',
    },
    modalRemoveVehicleConfig: {
      title: 'Remove vehicle',
      header: 'Are you sure you want to remove this vehicle?',
      subheader: '',
    },
    navBarConfig: {
      getLinks: ({
        isLoggedIn,
        hasLevelCreditAccount,
        levelCreditRedirectUrl,
      }) => {
        const loggedInMenuLinks = [
          {
            to: '/my-account',
            title: 'My policies',
            dataTestId: 'policies',
          },
          {
            to: '/my-activity',
            title: 'My payments',
            dataTestId: 'payments',
          },
          {
            to: '/my-profile',
            title: 'My profile',
            dataTestId: 'profile',
          },
          {
            to: process.env.REACT_APP_CLAIMS_URL!,
            title: 'Claims',
            dataTestId: 'claims',
          },
          {
            to: process.env.REACT_APP_FAQ_URL!,
            title: 'FAQs',
            dataTestId: 'faqs',
          },
          ...(hasLevelCreditAccount
            ? [
                {
                  to: levelCreditRedirectUrl,
                  title: 'Credit Lift',
                  dataTestId: 'credit_lift',
                },
              ]
            : []),
        ];

        const loggedOutMenuLinks = [
          {
            to: '/',
            title: 'Renters',
            dataTestId: 'renters',
            exact: true,
          },
          { to: '/auto', title: 'Auto', dataTestId: 'auto' },
          ...(isFeatureEnabled(featureFlag.IS_HOMEOWNERS_ENABLED)
            ? [
                {
                  to: '/homeowners',
                  title: 'Home',
                  dataTestId: 'home',
                },
              ]
            : []),
          {
            to: process.env.REACT_APP_CLAIMS_URL!,
            title: 'Claims',
            dataTestId: 'claims',
            target: '_blank',
          },
          {
            to: '',
            isDropdownMenu: true,
            dropdownLinks: [
              {
                to: process.env.REACT_APP_FAQ_URL!,
                title: 'FAQs',
                dataTestId: 'faqs',
                target: '_blank',
              },
              {
                to: 'https://petsbest.com/lp/toggle-pet-insurance',
                title: 'Pet insurance',
                dataTestId: 'pet',
                target: '_blank',
              },
              {
                to: 'https://info.gettoggle.com/benefits',
                title: 'Perks',
                dataTestId: 'perks',
                target: '_blank',
              },
              {
                to: process.env.REACT_APP_BLOG_URL!,
                title: 'Blog',
                dataTestId: 'blog',
                target: '_blank',
              },
              {
                to: process.env.REACT_APP_CONTACT_URL!,
                title: 'Contact Us',
                dataTestId: 'contact',
                target: '_blank',
              },
              {
                to: process.env.REACT_APP_ABOUT_US_URL!,
                title: 'About Us',
                dataTestId: 'about',
              },
            ],
            title: 'More',
            dataTestId: 'more',
          },
        ];

        const footerLinks = excludeFalse([
          isLoggedIn && {
            to: process.env.REACT_APP_ABOUT_US_URL!,
            title: 'About Us',
          },
          { to: '/terms', title: 'Terms' },
          { to: '/privacy', title: 'Privacy' },
        ]);

        return {
          main: isLoggedIn ? loggedInMenuLinks : loggedOutMenuLinks,
          footer: footerLinks,
          shouldHidePhoneLink: false,
          shouldHideSocialLinks: false,
          loginLogout: isLoggedIn
            ? { to: '/logout', title: 'Log out' }
            : { to: '/login', title: 'Log In' },
        };
      },
    },
    noAutoCoverageConfig: {
      ctaSubmitCopy: `Thanks! We'll be in touch as soon as Toggle is available in your state.`,
      getNoCoverageCopy: ({ regionName, isEligibleForRenters }) => {
        return isEligibleForRenters
          ? `We'd love to toggle with you, but we don't yet have auto insurance available in ${regionName}.`
          : `We'd love to toggle with you, but we're not in ${regionName} yet.`;
      },
      secondaryCopy: 'Let me know when Toggle comes my way',
    },
    notFoundPageConfig: {
      backgroundImgSrc: ufoImg,
      ctaHref: '/',
      isLightLogo: true,
    },
    paymentErrorPageConfig: {
      backgroundImgSrc: tenantCityImg,
      isLightLogo: true,
    },
    passwordValidator: {
      passingIconType: 'outline',
    },
    primaryVehicleDataSource: undefined,
    privacyPolicyView: {
      insuranceUrl: 'gettoggle.com',
      showPersonalInfoTable: true,
      showDoNotSellMyPersonalInfo: true,
      showSharingPersonalInformation: true,
      showYourChoices: true,
      firstBlockCmsFields: ['Introduction', 'Personal Information We Collect'],
      secondBlockCmsFields: [
        'How We Use Your Personal Information',
        'How We Protect Your Personal Information',
        'Personal Information We Disclose',
      ],
      thirdBlockCmsFields: [
        'Do Not Sell My Personal Information/Notice of Right to Opt Out',
        'Other Websites, Platforms and Applications',
        'Children and Minors',
        'Modifications to This Privacy Policy',
        'Cookies and Internet Advertising',
        'Your Rights to Access and Correction',
        'Signed',
      ],
    },
    purchaseFlowConfig: {
      checkoutExistingToggleCustomerAlreadyLoggedInCopy:
        "Looks like you're already a Toggle customer. Nice!",
      checkoutExistingToggleCustomerLoginSuccessCopy: 'Welcome back, Toggler!',
      checkoutExistingToggleCustomerPrompt:
        'Are you already a Toggle customer?',
      checkoutExistingToggleCustomerMessage: `Great! If you're an existing Toggle customer, you may be eligible for an additional discount.`,
      isCheckoutToggleCustomerDefault: undefined,
    },
    quoteAddOnsViewConfig: {
      nextButton: 'Next: Review & Pay',
    },
    quoteExpirationCondition: {
      maxElapsedTimeMinutes: 60,
      onConfirmDestination: '/auto/enroll/start',
      timestampKey: 'updated_at',
    },
    redirectUrls: {
      indexRedirectUrl: '',
      logoRedirectUrl: `${baseUrl}`,
      mfaSuccessRedirectUrl: `${mfaSuccessHost}my-account`,
      navBlockerRedirectUrl: `${baseUrl}auto/enroll/start`,
      isPartnerRedirectUrlEnabled: false,
    },
    reviewAutoQuoteConfig: {
      nextButtonCopy: 'Next: Signatures',
    },
    signUpViewConfig: {
      backgroundPicture: {
        alt: 'A man and his dog',
        sources: [
          {
            src: hatchbackDogImgWebP,
            type: 'image/webp',
          },
          {
            src: hatchbackDogImgPng,
            type: 'image/png',
          },
        ],
        fallbackSrc: hatchbackDogImgPng,
      },
      Hero: StandardHero,
      hasMainNav: true,
      heroVariant: 'across-page',
      title: 'Cover Your Stuff | Toggle Insurance',
      copy: {
        header:
          "Get auto insurance and peace of mind in minutes. Customize it so you don't have to pay for coverage you don't want.",
        hero1: 'Get in, ',
        hero2: "let's go.",
        informationalHeader: 'But first...',
        informationalSubHeader: 'Tell us a little about yourself.',
      },
    },
    stripeElStyles: {
      base: {
        '::placeholder': {
          color: '#9b9b9b',
        },
        color: '#000',
        fontFamily: "'GTHaptik', Helvetica, sans-serif",
        fontSize: '16px',
      },
      fonts: [
        {
          family: 'GTHaptik',
          // NOTE: this using relative URLs here causes issues with localhost (need https for non-mixed content), and e2ws, sandbox, and QA since they're password protected
          src: `url(https://sure-host.s3.amazonaws.com/static/farmers/GT-Haptik-Regular-2.ttf) format("truetype")`,
        },
      ],
      invalid: {
        color: '#000',
        iconColor: '#df436e',
      },
    },
    tenantDisplayName: 'Toggle',
    tenantPhoneNumber: '1-855-864-1530',
    utmSourceTenantPhoneNumberMap: {},
    vehicleManagementCopyConfig: {
      subheader:
        "We've added the vehicles from your current policy. Feel free to add more or edit our suggestions.",
      driverSubheader:
        'Feel free to edit the driver information shown below or add additional drivers. Also, please double check that all driver info is correct before moving on.',
    },
    autoDiscounts: [
      AUTO_DISCOUNTS.accidentPrevention,
      AUTO_DISCOUNTS.antiTheft,
      AUTO_DISCOUNTS.passiveRestraint,
      AUTO_DISCOUNTS.aeb,
      AUTO_DISCOUNTS.continuousInsurance,
      AUTO_DISCOUNTS.defensiveDriver,
      AUTO_DISCOUNTS.distantStudent,
      AUTO_DISCOUNTS.goodStudent,
      AUTO_DISCOUNTS.homeowner,
      AUTO_DISCOUNTS.multiCar,
      AUTO_DISCOUNTS.multiPolicy,
      AUTO_DISCOUNTS.accidentFree,
      AUTO_DISCOUNTS.safeDriving,
      AUTO_DISCOUNTS.teenDriver,
      AUTO_DISCOUNTS.threeYearSafeDriving,
      AUTO_DISCOUNTS.antiLockBrake,
    ],
    accidentPrevention: {
      getSubjectText: ({
        isPni,
        subjectText,
      }: Parameters<
        NonNullable<TConfig['accidentPrevention']['getSubjectText']>
      >[0]) => `${isPni ? 'Have you' : `Has ${subjectText}`}`,
      label: ' completed a motor vehicle accident prevention course?',
      explanation:
        'Approved accident prevention courses are designed to help improve driving skills and reduce driving risks by anticipating situations and making safe well-informed decisions. Courses required because of a moving traffic violation do not apply.',
    },
    defensiveDriver: {
      getSubjectText: ({
        isPni,
        subjectText,
      }: Parameters<
        NonNullable<TConfig['defensiveDriver']['getSubjectText']>
      >[0]) => `${isPni ? 'Have you' : `Has ${subjectText}`}`,
      label: ' taken a defensive driving course within the last 3 years?',
      explanation:
        'Defensive driving courses help improve driving skills and reduce driving risks by anticipating situations and making safe well-informed decisions. Courses required because of a driving offense or traffic violation do not apply.',
    },
    matureDriver: {
      getSubjectText: ({
        isPni,
        subjectText,
      }: Parameters<
        NonNullable<TConfig['matureDriver']['getSubjectText']>
      >[0]) => `${isPni ? 'Have you' : `Has ${subjectText}`}`,
      label:
        ' completed a mature driver motor vehicle accident prevention course?',
      explanation:
        'Approved defensive driving courses designed for mature drivers help improve driving skills and reduce driving risks by anticipating situations and making safe well-informed decisions. Courses required because of a driving offense or traffic violation do not apply.',
    },
    visibleCoverages: {
      protectionCoverages: [
        { label: 'Medical', key: 'medical_limit' },
        {
          label: 'Uninsured Motorist Bodily Injury',
          key: 'uninsured_motorist_bodily_injury_limit',
        },
        {
          label: 'Pet Passenger',
          key: 'pet_passenger',
          overrideValueLabel: '',
        },
      ],
      protectYourAssetsCoverages: [
        { label: 'Bodily Injury Liability', key: 'bodily_injury_limit' },
        { label: 'Property Damage Liability', key: 'property_damage_limit' },
      ],
      vehicleCoverages: [
        {
          label: 'Comprehensive & Collision',
          key: 'collision_deductible',
          overrideValueLabel: 'Covered after',
        },
      ],
    },
    supportEmail: 'support@gettoggle.com',
    formDisclaimer: (
      <>
        To prepare your personalized quote, we use information from you and 3rd
        party sources (like your credit, claims and/or driving histories).
        Don&apos;t worry, getting your price will not affect your credit score.
        To learn more about our information practices, including how to access
        and correct your information, please review our{' '}
        <Link key="privacy-policy-link" className="link" to="/privacy">
          Privacy Policy
        </Link>{' '}
        and{' '}
        <Link key="noip-link" className="link" to="/privacy">
          Notice of Information Practices
        </Link>
        .
        {isFeatureEnabled(featureFlag.IS_RECAPTCHA_ENABLED) && (
          <div>
            This site is protected by reCAPTCHA and the Google{' '}
            <a href="https://policies.google.com/privacy">Privacy Policy</a> and{' '}
            <a href="https://policies.google.com/terms">Terms of Service</a>{' '}
            apply.
          </div>
        )}
        {isFeatureEnabled(featureFlag.IS_AUTO_HCAPTCHA_ENABLED) && (
          <div>
            This site is protected by hCaptcha and its{' '}
            <a href="https://www.hcaptcha.com/privacy">Privacy Policy</a> and{' '}
            <a href="https://www.hcaptcha.com/terms">Terms of Service</a> apply.
          </div>
        )}
      </>
    ),
    initialCoverageValues: {
      collision_deductible: '0',
      new_car_replacement: 'false',
      oem_parts: 'false',
      towing_road_services: 'false',
    },
    sideEffectFunctionMessages: {},
    vehicleSideEffectMessages: {
      towing_road_services: defaultVehicleSideEffectMessage,
      loss_of_use: defaultVehicleSideEffectMessage,
      loan_lease_payoff:
        'This coverage is only available if Comprehensive & Collision is also selected, the vehicle is financed or leased, and the model year is three (3) years old or less.',
      oem_parts:
        'This coverage is only available if Comprehensive & Collision is also selected and the model year is ten (10) years old or less.',
      new_car_replacement:
        'This coverage is only available if Comprehensive & Collision is also selected and the model year is one (1) year old or less.',
      temporary_ride: defaultVehicleSideEffectMessage,
      side_hustle: defaultVehicleSideEffectMessage,
      gap: 'This coverage is only available if Comprehensive & Collision is also selected, the vehicle is financed or leased, and the model year is three (3) years old or less.',
      glass: defaultVehicleSideEffectMessage,
      uninsured_motorist_property_damage_limit: defaultVehicleSideEffectMessage,
    },
    selfServiceConfig: {
      promoCardParagraphText: 'Save on auto when you combo.',
    },
    billingView: {
      header: 'Billing',
    },
  };
}

async function retrieveConfig(filename = '') {
  try {
    const module = await import(`./whitelabel/${filename}`);
    return module.default;
  } catch (e) {
    return null;
  }
}

async function retrievePartnerConfig(opts: TConfigParams, filename = '') {
  const partnerConfig = await retrieveConfig(filename);
  return handleFunctionConfig(partnerConfig, opts);
}

async function getEnvConfig(opts: TConfigParams, overrideFileName = '') {
  if (isServer || !process.env.REACT_APP_WL_CONFIG) {
    return {};
  }

  const { wlConfigTestHelper, Cypress } = window;

  const configFileName = process.env.REACT_APP_WL_CONFIG;
  const config = await ((overrideFileName &&
    retrieveConfig(overrideFileName)) ||
    retrievePartnerConfig(opts, configFileName));

  return (
    (Cypress && wlConfigTestHelper && wlConfigTestHelper(config)) ||
    config ||
    {}
  );
}

export async function generateMergedConfig(
  configParams: TConfigParams = {
    isFeatureEnabled: () => false,
    isAppConfigFeatureEnabled: () => false,
  },
  overrideFileName = ''
): Promise<TConfig> {
  return regionConfigMerge({
    defaultConfig: generateConfig(configParams),
    newConfig: await getEnvConfig(configParams, overrideFileName),
  });
}

export function generateWlConfig(
  configParams: TConfigParams = {
    isFeatureEnabled: () => false,
    isAppConfigFeatureEnabled: () => false,
  }
): TConfig {
  return generateConfig(configParams);
}

export function getFinalMergedConfig() {
  return generateMergedConfig();
}
